import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "mtr_cookie_consent_v1";

const defaultPrefs = { essential: true, functional: false, analytics: false };

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const saved = loadPrefs();
    if (!saved) setVisible(true);
    else setPrefs(saved);
  }, []);

  const openSettings = useCallback(() => {
    setPrefs(loadPrefs() || defaultPrefs);
    setShowSettings(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, [openSettings]);

  const persist = (p) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...p, essential: true, ts: Date.now() }));
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist({ essential: true, functional: true, analytics: true });
  const rejectAll = () => persist({ essential: true, functional: false, analytics: false });
  const saveChoices = () => persist(prefs);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5" data-testid="cookie-consent">
      <div className="container-mtr">
        <div className="bg-white rounded-card shadow-modal border border-gravel overflow-hidden">
          {!showSettings ? (
            <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="text-orange shrink-0 mt-0.5" size={24} aria-hidden="true" />
                <p className="text-[14px] text-iron leading-relaxed">
                  We use cookies to run the site (essential) and, with your consent, to improve it.
                  Under the CPRA you can opt out of analytics and the sale/sharing of your information.{" "}
                  <Link to="/cookie-policy" className="text-orange font-semibold underline">Learn more</Link>.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 lg:shrink-0">
                <button onClick={() => setShowSettings(true)} className="btn-ghost !min-h-[40px] !py-2" data-testid="cookie-customize">
                  Customize
                </button>
                <button onClick={rejectAll} className="btn-secondary !min-h-[40px] !py-2" data-testid="cookie-reject-all">
                  Reject non-essential
                </button>
                <button onClick={acceptAll} className="btn-primary !min-h-[40px] !py-2" data-testid="cookie-accept-all">
                  Accept all
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 sm:p-6" data-testid="cookie-settings-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-body font-extrabold text-navy text-[20px]">Cookie preferences</h2>
                <button onClick={() => setVisible(false)} aria-label="Close" data-testid="cookie-close" className="text-mortar hover:text-iron">
                  <X size={22} />
                </button>
              </div>
              <div className="space-y-3">
                <Row title="Strictly necessary" desc="Required for sign-in, security, and core functionality. Always on." checked disabled testid="cookie-essential" />
                <Row
                  title="Functional"
                  desc="Remembers preferences like recent addresses and waste-factor settings."
                  checked={prefs.functional}
                  onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                  testid="cookie-functional"
                />
                <Row
                  title="Analytics & sharing"
                  desc="Helps us understand usage. Opting out here also opts you out of the sale/sharing of your personal information under the CPRA."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testid="cookie-analytics"
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-5 justify-end">
                <button onClick={rejectAll} className="btn-ghost !min-h-[40px] !py-2" data-testid="cookie-reject-all-2">Reject non-essential</button>
                <button onClick={saveChoices} className="btn-primary !min-h-[40px] !py-2" data-testid="cookie-save">Save choices</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ title, desc, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-input bg-fieldstone">
      <div>
        <p className="font-bold text-iron text-[15px]">{title}</p>
        <p className="text-[13px] text-mortar mt-0.5 max-w-xl">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        data-testid={`${testid}-toggle`}
        className={`mt-1 shrink-0 w-12 h-7 rounded-full transition-colors ${checked ? "bg-orange" : "bg-gravel"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span className={`block w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </div>
  );
}

export default CookieConsent;
